import { useEffect, useEffectEvent, useRef } from 'react';

import type { GenUiDispatch } from '../../../shared/ui/action.js';
import {
  type ChatUiHotelOffer,
  type HotelPlannerSelection,
  policyLimitsFromOffers,
  toHotelRefineOffer,
} from '@voll-intelligence/genui-contract';
import type { HotelSearchPreviewState } from './use_hotel_search_preview.js';
import type { PanelReport } from './use_panel_report.js';

/** O relato do painel (M1 · 1.1b) some da CONVERSA em rajada: a cada tecla no
 *  filtro/ordenador o organismo reporta um `screen` novo, e sem atraso cada
 *  toque viraria um `hotel-search-context` — o mesmo `POST /messages` que o
 *  refino de texto usa. O RESULTADO (busca concluída) continua imediato, como
 *  na Onda 1: é o gesto que fecha um turno de leitura, não ruído de digitação. */
export const PANEL_REPORT_DEBOUNCE_MS = 400;

/** De onde sai o contexto a reportar: `client` = a busca client-side concluída
 *  (seleção/sessão/ofertas do RESULTADO); `block` = o card do agente sem busca
 *  client-side (#2857: seleção e sessão do BLOCO, sem ofertas); `null` = nada
 *  a sincronizar (só-leitura, superado, busca em voo, bloco sem seleção). */
type SyncTarget = {
  mode: 'client' | 'block' | null;
  selection: HotelPlannerSelection | undefined;
  sessionId: string | undefined;
  offers: readonly ChatUiHotelOffer[] | undefined;
};

const NO_TARGET: SyncTarget = {
  mode: null,
  selection: undefined,
  sessionId: undefined,
  offers: undefined,
};

function syncTarget(input: {
  challengeId: string;
  state: HotelSearchPreviewState;
  blockSessionId: string | undefined;
  blockSelection: HotelPlannerSelection | undefined;
  readOnly: boolean;
  superseded: boolean;
}): SyncTarget {
  const { state } = input;
  if (input.readOnly || input.superseded) return NO_TARGET;
  const clientReady =
    state.challengeId === input.challengeId &&
    state.phase === 'ready' &&
    state.selection !== undefined;
  if (clientReady) {
    return {
      mode: 'client',
      selection: state.selection,
      sessionId: state.data?.sessionId ?? input.blockSessionId,
      offers: state.data?.offers,
    };
  }
  if (input.blockSelection !== undefined) {
    return {
      mode: 'block',
      selection: input.blockSelection,
      sessionId: input.blockSessionId,
      offers: undefined,
    };
  }
  return NO_TARGET;
}

/**
 * Sincroniza o resultado da busca CLIENT-SIDE do card com o host (#2477). A busca
 * feita dentro do `hotel_planner` é uma leitura (`POST /hotel/search-preview`, sem
 * turno), então o FSM do agente nunca aprende os critérios/sessão/ofertas que o
 * viajante viu. Quando uma busca conclui (`phase: 'ready'`) num card VIVO,
 * despachamos `hotel-search-context` — o host GUARDA o trio (critérios + sessão +
 * ofertas em cena) e o ANEXA ao próximo refino de TEXTO, para o agente hidratar o
 * FSM e projetar a cena com o que o card mostra, em vez de responder
 * `missing_criteria`/negar ofertas visíveis e reemitir um planner vazio.
 *
 * Uma vez por resultado (chave = challengeId + seleção + sessão): sem a guarda,
 * todo render re-despacharia o mesmo contexto. Card superado/resolvido não
 * sincroniza — ali a busca já vira turno (`onTurnSearch`), que leva os critérios
 * ao FSM pelo caminho normal.
 *
 * #2857 — o card EMITIDO PELO AGENTE (busca por texto, sem busca client-side)
 * também reporta o PAINEL: o viajante abre "Ver todos os hotéis", filtra, fecha
 * e pergunta "quais sobraram?" — sem o relato o retrato não tem a linha Painel
 * e o agente inventava um filtro. Neste caminho a `selection` é a do BLOCO
 * (`blockSelection`, destino + estadia do card), a sessão é a do bloco e as
 * ofertas NÃO viajam (o FSM já tem as do card que ele mesmo emitiu). Só depois
 * de um GESTO no painel (há `panel` no relato): um relato neutro desde a
 * montagem guardaria no host um contexto que sairia em TODO turno de texto —
 * e cada hidratação tira o agente do modo consultoria. Uma vez reportado, a
 * volta ao neutro (filtro limpo) também vai: o host guarda o ÚLTIMO relato.
 * A busca client-side que conclui depois assume (dispara na hora, como sempre).
 */
export function useHotelSearchContextSync({
  dispatch,
  challengeId,
  blockId,
  state,
  blockSessionId,
  readOnly,
  superseded,
  screen,
  blockSelection,
}: {
  dispatch: GenUiDispatch;
  challengeId: string;
  /** `ui.id` do planner — ver `HotelPlannerRefineContext.blockId`. */
  blockId: string;
  state: HotelSearchPreviewState;
  /** A sessão do bloco emitido — o fallback quando a resposta da busca não trouxe
   *  uma (o host reusou a do bloco em vez de mintar outra). */
  blockSessionId: string | undefined;
  /** Card só-leitura (conversa encerrada) ou superado: a busca lá já vira turno
   *  (`onTurnSearch`), que leva o contexto ao FSM pelo caminho normal — não
   *  sincroniza. Somados AQUI (não no card) para não custar complexidade lá. */
  readOnly: boolean;
  superseded: boolean;
  /** O painel/total que o card monta (organismo + gaveta) — M1 · 1.1b. */
  screen: PanelReport;
  /** #2857: a seleção que o BLOCO representa (`blockSelection`) — o `selection`
   *  do relato do painel quando não há busca client-side. Ausente (bloco sem
   *  destino/estadia) = o card sem busca client-side fica calado, como antes. */
  blockSelection?: HotelPlannerSelection;
}): void {
  const lastResultKey = useRef<string | null>(null);
  const lastScreenKey = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** #2857: já houve UM relato pelo caminho do bloco — a partir dele a volta ao
   *  painel neutro também é reportada. `lastBlockKey` é a dedupe desse caminho
   *  (tela + seleção + sessão do bloco). */
  const blockReported = useRef(false);
  const lastBlockKey = useRef<string | null>(null);

  // `undefined`/`null` quando não há nada a sincronizar (guarda idêntica à de
  // antes). São STRINGS — não os objetos `state`/`screen` em si — porque
  // essas chaves são o que os dois efeitos abaixo escutam: `dispatch` NÃO
  // entra nas dependências deles (ver `send`, embaixo) por ser recriado a
  // cada render do HOST (`MessageRow` não é memoizado e chama
  // `createDispatch({...})` dentro de `anchored.map(...)` — #2628 finding). Se
  // o efeito da tela dependesse do objeto `screen`/`state`/`dispatch` inteiros,
  // qualquer render alheio durante os 400 ms (texto do assistente chegando em
  // streaming, por exemplo) cancelaria e reiniciaria o timer, adiando o
  // relato indefinidamente — silenciosamente, porque o dispatch ainda
  // acontece mais cedo ou mais tarde, só nunca a tempo do próximo refino.
  const target = syncTarget({
    challengeId,
    state,
    blockSessionId,
    blockSelection,
    readOnly,
    superseded,
  });
  // #2857: o caminho do BLOCO — só enquanto nenhuma busca client-side concluiu.
  const blockActive = target.mode === 'block';
  const resultKey =
    target.mode === 'client'
      ? JSON.stringify([challengeId, target.selection, target.sessionId ?? null])
      : null;
  const screenKey = target.mode === null ? null : JSON.stringify(screen);
  // A chave do relato do BLOCO leva seleção e sessão além da tela (CodeRabbit,
  // #3290): a reemissão com o MESMO id atualiza o card no lugar, e o bloco
  // pode trocar sessão/critérios sem a tela mudar — sem isso o relato seguinte
  // não saía e o host guardava o contexto velho.
  const blockKey = blockActive
    ? JSON.stringify([blockId, target.selection, target.sessionId ?? null, screen])
    : null;

  // O relato lê o valor mais recente de tudo que o dispatch (imediato OU adiado)
  // precisa na hora em que dispara: um timer agendado num commit anterior despacha
  // com o `dispatch`/`screen` atuais, nunca com uma versão obsoleta — e nada disso
  // entra nas dependências dos efeitos.
  const send = useEffectEvent(() => {
    const { selection, offers, sessionId } = target;
    if (!selection) return;
    // Os limites da política são do VIAJANTE, iguais em toda oferta: viajam
    // UMA vez no contexto (ver `policyLimitsFromOffers`), fora das ofertas.
    const policyLimits = policyLimitsFromOffers(offers ?? []);
    dispatch({
      type: 'hotel-search-context',
      challengeId,
      blockId,
      selection,
      ...(sessionId ? { sessionId } : {}),
      // As mesmas ofertas que o card exibe (#2477 gap): sem elas o host anexa só
      // critérios/sessão e o agente projeta uma cena vazia sobre uma lista que o
      // viajante já vê. `.max(30)` espelha o teto de `HotelPlannerRefineContext`
      // (`hotel_planner_schema.ts`) — `state.data.offers` já vem capado bem abaixo
      // disso (`CHAT_UI_MAX_OFFERS`), o slice é só defensivo. Projetadas para a
      // forma ENXUTA aqui, na origem (#2628): fotos/quartos/descrição nunca saem do
      // card — o host não conhece a forma da oferta, e o `POST /messages` que
      // carrega o contexto tem de caber nos 8 KB do WAF.
      ...(offers && offers.length > 0
        ? { offers: offers.slice(0, 30).map(toHotelRefineOffer) }
        : {}),
      ...(policyLimits ? { policyLimits } : {}),
      ...(screen.panel ? { panel: screen.panel } : {}),
      ...(screen.total === undefined ? {} : { total: screen.total }),
      // Consequência automática de uma busca concluída, não um gesto direto — o
      // chokepoint de telemetria do host marca a diferença (espelha `planner-preview`).
      analytics: { trigger: 'auto' },
    });
  });
  const touched = useEffectEvent(() => screen.panel !== undefined);

  // Resultado NOVO (busca concluída/refinada): dispara na hora, como na Onda 1.
  // Cancela um debounce de TELA pendente — o próprio dispatch já leva a tela
  // atual, então não há mais nada a relatar depois.
  useEffect(() => {
    if (resultKey === null || lastResultKey.current === resultKey) return;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    send();
    lastResultKey.current = resultKey;
    lastScreenKey.current = screenKey;
  }, [resultKey, screenKey]);

  // Só a TELA mudou (mesmo resultado): debounce de `PANEL_REPORT_DEBOUNCE_MS`.
  // Dependência é `screenKey` (a STRING de conteúdo), não `screen` — um
  // re-render que troca a identidade de `dispatch`/`state`/`screen` sem mudar
  // o CONTEÚDO não passa por aqui, então não reinicia o timer.
  useEffect(() => {
    if (resultKey === null || screenKey === null) return undefined;
    if (lastResultKey.current !== resultKey || lastScreenKey.current === screenKey)
      return undefined;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      send();
      lastScreenKey.current = screenKey;
    }, PANEL_REPORT_DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [resultKey, screenKey]);

  // #2857: o caminho do BLOCO (card do agente, sem busca client-side) — TUDO é
  // tela, então tudo passa pelo debounce. O portão é o GESTO: só reporta quando
  // há `panel` no relato (o viajante abriu/filtrou/ordenou/abriu a gaveta) ou
  // quando já reportou antes (a volta ao neutro também conta). `screen.panel` é
  // lido na hora (`touched`; o conteúdo é o de `blockKey`, a dependência).
  useEffect(() => {
    if (blockKey === null) return undefined;
    if ((!touched() && !blockReported.current) || lastBlockKey.current === blockKey)
      return undefined;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      send();
      blockReported.current = true;
      lastBlockKey.current = blockKey;
    }, PANEL_REPORT_DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [blockKey]);
}
